<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onServerPrefetch, useSSRContext, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { fetchAgencies } from '@/api/customerMetaApi';
import DirectoryBreadcrumbs from '@/components/Breadcrumbs/DirectoryBreadcrumbs.vue';
import DirectorySeoLinks from '@/components/DirectorySeoLinks/DirectorySeoLinks.vue';
import { useDirectoryRouteLocations } from '@/composables/useDirectoryRouteLocations';
import { useDirectorySrpJsonLdContext } from '@/composables/useDirectorySrpJsonLdContext';
import { useAgencySrpJsonLdHead } from '@/composables/useDirectorySrpJsonLdHead';
import { isPlatformHg, isPlatformIs24 } from '@/helpers/Platform/platform';
import {
  buildAgencyDirectorySeoLinks,
  getAgencyDirectoryPageDescription,
  getAgencyDirectoryPageTitle,
  getDirectoryLocationsSeoLabel,
} from '@/helpers/SEO/agencyDirectorySeoText';
import { buildDirectoryCanonicalUrl } from '@/helpers/SEO/directoryCanonicalUrl';
import {
  sanitizeListingTypeParam,
  sanitizeSortByParam,
} from '@/helpers/String/sanitizeQueryParams';
import AgencyResultList from '@/pages/AgencySRP/AgencyResultList.vue';
import HeroBanner from '@/pages/AgencySRP/HeroBanner.vue';
import { syncAgencyDirectoryFromRoute } from '@/pages/AgencySRP/syncAgencyDirectoryFromRoute';
import { useLocale } from '@/router/routeMeta';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';
import { LoadState } from '@/stores/types';
import {
  buildAgencyDirectoryPageViewPayload,
  trackAgencyDirectoryPageView,
} from '@/tracking/tracking';

const { t } = useI18n();
const language = useLocale();
const route = useRoute();
const ssrContext = useSSRContext();

const agencyProfilesStore = useAgencyProfilesStore();
const {
  agencies,
  agenciesLoadState,
  totalAgenciesCount,
  agencyListCurrentPage,
  pageCount,
  searchedLocations,
} = storeToRefs(agencyProfilesStore);

const currentPage = computed(() => {
  const page = parseInt((route.query.page as string) ?? 1, 10);

  if (isNaN(page) || page < 1) {
    return 1;
  }

  return page;
});

const filterLocations = useDirectoryRouteLocations();

const includeChildrenAgencies = computed(() => route.query.includeChildren === 'true');
const listingType = computed(() => sanitizeListingTypeParam(route.query.listingType));
const sortingOption = computed(() => sanitizeSortByParam(route.query.sortBy));

const syncFromRoute = () =>
  syncAgencyDirectoryFromRoute({
    geoLocations: filterLocations.value,
    locale: language.value,
    includeChildren: includeChildrenAgencies.value,
    currentPage: currentPage.value,
    listingType: listingType.value,
    sortBy: sortingOption.value,
  });

const syncAgencyQueryStashFromRoute = () => {
  agencyProfilesStore.syncLastAgencyDirectoryQueryFromRoute(route.query);
};

const loadAgencyFromRoute = async () => {
  syncAgencyQueryStashFromRoute();
  const didApplyFilters = await syncFromRoute();

  if (!didApplyFilters) {
    return;
  }

  trackAgencyDirectoryPageView({
    agencies: agencies.value,
    filterLocations: filterLocations.value,
    totalAgenciesCount: totalAgenciesCount.value,
    pageCount: pageCount.value,
    currentPage: currentPage.value,
    language: language.value,
    listingType: listingType.value,
    sortingOption: sortingOption.value,
  });
};

onServerPrefetch(async () => {
  syncAgencyQueryStashFromRoute();
  await syncFromRoute();

  if (ssrContext) {
    const pageTrackingContext = buildAgencyDirectoryPageViewPayload({
      agencies: agencies.value,
      filterLocations: filterLocations.value,
      totalAgenciesCount: totalAgenciesCount.value,
      pageCount: pageCount.value,
      currentPage: currentPage.value,
      language: language.value,
      listingType: listingType.value,
      sortingOption: sortingOption.value,
    });
    ssrContext.pageTrackingContext = JSON.stringify(pageTrackingContext);
  }
});

watch(
  [currentPage, filterLocations, includeChildrenAgencies, listingType, sortingOption],
  async () => {
    await loadAgencyFromRoute();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  agencyProfilesStore.markSearchedLocationsStale();
});

const generateLocationsForSeo = () =>
  getDirectoryLocationsSeoLabel(searchedLocations.value, language.value);

const generateCanonicalUrlForSeo = () => buildDirectoryCanonicalUrl(route.path, currentPage.value);

const generatePageTitleForSeo = () =>
  getAgencyDirectoryPageTitle(t, currentPage.value, generateLocationsForSeo());

const generatePageDescriptionForSeo = () =>
  getAgencyDirectoryPageDescription(t, generateLocationsForSeo());

const { locationCount, breadcrumbs, isIndexableForJsonLd } = useDirectorySrpJsonLdContext(
  'agency',
  searchedLocations,
  currentPage,
);

const seoLinks = computed(() =>
  buildAgencyDirectorySeoLinks(generateCanonicalUrlForSeo(), searchedLocations.value),
);

// Pre-GA Agency SRP: unconditional noindex for client-side navigations (Lambda sets X-Robots-Tag on SSR).
useHead({
  title: () => generatePageTitleForSeo(),
  meta: () => [
    {
      name: 'description',
      content: () => generatePageDescriptionForSeo(),
    },
    { name: 'robots', content: 'noindex, nofollow' },
    { 'http-equiv': 'x-robots-tag', content: 'noindex, nofollow' },
  ],
  link: seoLinks,
});

useAgencySrpJsonLdHead(() => ({
  language: language.value,
  canonicalUrl: generateCanonicalUrlForSeo(),
  pageTitle: generatePageTitleForSeo(),
  pageDescription: generatePageDescriptionForSeo(),
  breadcrumbs: breadcrumbs.value,
  isIndexable: isIndexableForJsonLd.value,
  locationCount: locationCount.value,
  agencies: agencies.value,
  agencyListCurrentPage: agencyListCurrentPage.value,
  totalAgenciesCount: totalAgenciesCount.value,
  loadingAgencies: agenciesLoadState.value === LoadState.LOADING,
}));
</script>

<template>
  <div class="layout-container">
    <DirectoryBreadcrumbs
      v-if="isPlatformIs24() && searchedLocations !== null"
      directoryType="agency"
      :searchedLocations="searchedLocations ?? []"
      class="breadcrumbs"
    />
  </div>

  <HeroBanner />

  <div class="layout-container">
    <AgencyResultList
      :loadingAgencies="agenciesLoadState === LoadState.LOADING"
      :agencies="agencies"
      :totalAgenciesCount="totalAgenciesCount"
      :agencyListCurrentPage="agencyListCurrentPage"
      :pageCount="pageCount"
      :language="language"
      :locationsForSeo="generateLocationsForSeo()"
    />
    <DirectoryBreadcrumbs
      v-if="isPlatformHg() && searchedLocations !== null"
      directoryType="agency"
      :searchedLocations="searchedLocations ?? []"
      class="breadcrumbs"
    />
    <DirectorySeoLinks
      directoryType="agency"
      :searchedLocations="searchedLocations ?? []"
      :inventoryFetcher="fetchAgencies"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/variables' as *;

.breadcrumbs {
  margin-top: $space-150;
}
</style>
